<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12" md="4">
        <v-card v-for="chart in donutCharts" :key="chart.id" class="card mb-2" color="primary">
          <v-card-subtitle class="text-center font-weight-bold secondary--text pb-0">
            {{ chart.title }}
          </v-card-subtitle>
          <v-card-text class="pa-1">
            <apexchart
              v-if="chart.data.series.length > 0"
              width="100%"
              height="200"
              type="donut"
              :options="chart.data.options"
              :series="chart.data.series"
            />
            <div v-else class="text-center text-caption secondary--text py-4">Sin datos</div>
            <v-list v-if="chart.totals.length > 0" dense class="status-totals-list pa-0 mt-2" color="transparent">
              <v-list-item v-for="entry in chart.totals" :key="entry.label" class="px-3 status-totals-item">
                <v-list-item-content class="py-1">
                  <div class="d-flex justify-space-between align-center">
                    <span class="text-caption">{{ entry.label }}</span>
                    <span class="text-caption font-weight-bold secondary--text">{{ entry.count }}</span>
                  </div>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="status-totals-divider"></v-divider>
              <v-list-item class="px-3 status-totals-item">
                <v-list-item-content class="py-1">
                  <div class="d-flex justify-space-between align-center">
                    <span class="text-caption font-weight-bold">Total</span>
                    <span class="text-caption font-weight-bold secondary--text">{{ chart.total }}</span>
                  </div>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="8">
        <v-card color="primary" style="height: 100%">
          <v-card-subtitle class="text-center font-weight-bold secondary--text pb-0">Por item</v-card-subtitle>
          <v-card-text class="pa-1">
            <apexchart
              v-if="itemChartData.series[0].data.length > 0"
              width="100%"
              height="420"
              type="bar"
              :options="itemChartData.options"
              :series="itemChartData.series"
            />
            <div v-else class="text-center text-caption secondary--text py-4">Sin datos</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
const MONOCHROME_THEME = {
  mode: 'light',
  monochrome: {
    enabled: true,
    color: '#ff9f43',
    shadeTo: 'light',
    shadeIntensity: 0.65,
  },
}

const CHART_TEXT_COLOR = '#e0e0e0'
const CHART_GRID_COLOR = 'rgba(224, 224, 224, 0.18)'

function buildDonutData(equipos, keyExtractor) {
  const counts = {}
  for (const equipo of equipos) {
    const key = keyExtractor(equipo) || 'Sin datos'
    counts[key] = (counts[key] || 0) + 1
  }
  const labels = Object.keys(counts)
  return {
    series: labels.map((k) => counts[k]),
    options: {
      chart: { type: 'donut', background: 'transparent', foreColor: CHART_TEXT_COLOR },
      labels,
      legend: {
        position: 'bottom',
        fontSize: '10px',
        labels: { colors: CHART_TEXT_COLOR },
        itemMargin: { horizontal: 4, vertical: 0 },
      },
      dataLabels: {
        enabled: true,
        style: { colors: [CHART_TEXT_COLOR], fontSize: '11px', fontWeight: 600 },
      },
      tooltip: { theme: 'dark' },
      theme: MONOCHROME_THEME,
    },
  }
}

function buildBarData(equipos, itemExtractor, statusExtractor) {
  const totals = {}
  const matrix = {}
  const statusSet = new Set()
  for (const equipo of equipos) {
    const item = itemExtractor(equipo) || 'Sin datos'
    const status = statusExtractor(equipo) || 'Sin estatus'
    statusSet.add(status)
    totals[item] = (totals[item] || 0) + 1
    if (!matrix[item]) matrix[item] = {}
    matrix[item][status] = (matrix[item][status] || 0) + 1
  }
  const categories = Object.keys(totals).sort((a, b) => totals[b] - totals[a])
  const statuses = [...statusSet].sort()
  const series = statuses.map((status) => ({
    name: status,
    data: categories.map((item) => matrix[item]?.[status] || 0),
  }))
  return {
    series,
    options: {
      chart: { type: 'bar', stacked: true, background: 'transparent', foreColor: CHART_TEXT_COLOR },
      plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
      grid: { borderColor: CHART_GRID_COLOR },
      xaxis: {
        categories,
        labels: { style: { colors: CHART_TEXT_COLOR, fontSize: '11px' } },
      },
      yaxis: {
        labels: { style: { colors: CHART_TEXT_COLOR, fontSize: '10px' } },
      },
      legend: {
        position: 'bottom',
        labels: { colors: CHART_TEXT_COLOR },
      },
      dataLabels: {
        enabled: true,
        style: { colors: [CHART_TEXT_COLOR], fontSize: '11px', fontWeight: 700 },
      },
      tooltip: { theme: 'dark' },
      theme: MONOCHROME_THEME,
    },
  }
}

export default {
  name: 'EstadisticasEquipoComponent',

  props: {
    equipos: {
      type: Array,
      default: () => [],
    },
  },

  computed: {
    donutCharts() {
      const data = buildDonutData(this.equipos, (e) => e?.CatEstatus?.title)
      const totals = data.options.labels
        .map((label, index) => ({ label, count: data.series[index] }))
        .sort((a, b) => b.count - a.count)
      const total = totals.reduce((acc, entry) => acc + entry.count, 0)
      return [
        {
          id: 'estatus',
          title: 'Por estatus',
          data,
          totals,
          total,
        },
      ]
    },
    itemChartData() {
      return buildBarData(
        this.equipos,
        (e) => e?.CatItem?.item,
        (e) => e?.CatEstatus?.title
      )
    },
  },
}
</script>

<style scoped>
.status-totals-list {
  background: transparent !important;
}
.status-totals-item {
  min-height: 28px !important;
  color: #e0e0e0;
}
.status-totals-divider {
  border-color: rgba(255, 159, 67, 0.25) !important;
}
</style>
